// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Coordinates validated Contract Review agent launches and their UI lifecycle.
 *
 * One user action may fan out across agents and documents. This hook owns that
 * queueing boundary, assigns every returned job to its document/agent pair, and
 * clears each optimistic document/agent pair when its own job completes.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import type { NodeApiClient } from '@/app-shell/services'
import type { Agent } from '@/subsystems/agents'
import { useTrackedOperation } from '@/subsystems/jobs'
import { runPipelines } from '@/subsystems/pipelines'

import type { DocumentListItemWithPipelines } from '../models'
import { agentAcceptsDocuments } from '../lib/agentRunRules'
import type { RunVersions } from '../components/WorkspaceRunDialog'
import { useWorkspaceRunStatus, type WorkspaceRunEntry } from '../components/WorkspaceRunStatus'

/**
 * A job still occupies its document's spinner only while it can still change.
 * Anything else — COMPLETED, ERROR, or a status we don't recognise — is over as
 * far as that one document is concerned.
 */
function jobIsSettled(status: string) {
  return status !== 'PENDING' && status !== 'RUNNING'
}

function workspaceRunErrorMessage(error: unknown) {
  const detail = (error as {
    response?: { data?: { detail?: string | { message?: string } } }
  })?.response?.data?.detail
  return typeof detail === 'string'
    ? detail
    : detail?.message ?? (error instanceof Error ? error.message : String(error))
}

interface UseWorkspaceRunsProps {
  apiClient: NodeApiClient
  orgId: string
  workspaceId: string
  agents: Agent[]
  documents: DocumentListItemWithPipelines[]
  onStatusChange: () => void
  onAllComplete: (erroredJobIds: string[]) => void
  onError: (message: string) => void
}

export function useWorkspaceRuns({
  apiClient,
  orgId,
  workspaceId,
  agents,
  documents,
  onStatusChange,
  onAllComplete,
  onError,
}: UseWorkspaceRunsProps) {
  const [pending, setPending] = useState<{
    agentIds: string[]
    documents: DocumentListItemWithPipelines[]
  } | null>(null)
  const [optimisticRunning, setOptimisticRunning] = useState<Map<string, Set<string>>>(new Map())
  // Which (document, agent) each tracked job belongs to, so completing one
  // batch only clears its own optimistic spinners — not a concurrent batch's.
  const jobOwnership = useRef<Map<string, { documentId: string; agentId: string }>>(new Map())

  const clearOptimistic = useCallback((pairs: { documentId: string; agentId: string }[]) => {
    setOptimisticRunning((current) => {
      const next = new Map(current)
      for (const { documentId, agentId } of pairs) {
        const running = new Set(next.get(documentId) ?? [])
        running.delete(agentId)
        if (running.size > 0) next.set(documentId, running)
        else next.delete(documentId)
      }
      return next
    })
  }, [])

  const { runTracked, isRunning, jobStatuses, enqueueJobs } = useTrackedOperation({
    apiClient,
    orgId,
    workspaceId,
    onStatusChange,
    onAllComplete: ({ erroredJobIds }) => onAllComplete(erroredJobIds),
  })
  const status = useWorkspaceRunStatus({ orgId, workspaceId, jobStatuses, enqueueJobs })

  // cm-coding: Each document spinner represents one agent job and must stop as
  // soon as that job settles. Remove completed ownership entries individually;
  // waiting for the whole batch would leave finished documents appearing busy
  // until their slowest sibling completes.
  useEffect(() => {
    const settled = Object.entries(jobStatuses)
      .filter(([jobId, jobStatus]) => jobIsSettled(jobStatus) && jobOwnership.current.has(jobId))
    if (settled.length === 0) return
    const pairs = settled.map(([jobId]) => {
      const owner = jobOwnership.current.get(jobId)!
      jobOwnership.current.delete(jobId)
      return owner
    })
    clearOptimistic(pairs)
  }, [jobStatuses, clearOptimistic])

  const openDocuments = useCallback((
    agentIds: string[],
    selectedDocuments: DocumentListItemWithPipelines[],
  ) => {
    const selectedAgents = agents.filter((agent) => agentIds.includes(agent.agentId))
    if (selectedAgents.length !== agentIds.length) {
      onError('Run selection contains an unknown agent or document.')
      return
    }
    const incompatible = selectedAgents.find((agent) =>
      !agent.pipelineId || !agentAcceptsDocuments(agent, selectedDocuments),
    )
    if (incompatible) {
      onError(`${incompatible.label} cannot run on one or more selected documents.`)
      return
    }
    setPending({ agentIds, documents: selectedDocuments })
  }, [agents, onError])

  const open = useCallback((agentIds: string[], documentIds: string[]) => {
    const selectedDocuments = documents.filter((document) => documentIds.includes(document.documentId))
    if (selectedDocuments.length !== documentIds.length) {
      onError('Run selection contains an unknown agent or document.')
      return
    }
    openDocuments(agentIds, selectedDocuments)
  }, [documents, onError, openDocuments])

  const confirm = useCallback(async (versions: RunVersions) => {
    if (!pending) return
    const { agentIds, documents: selectedDocuments } = pending
    const documentIds = selectedDocuments.map((document) => document.documentId)
    const selectedAgents = agents.filter((agent) => agentIds.includes(agent.agentId))
    const invalidComparison = selectedAgents
      .filter((agent) => agent.pipelineId === 'version-diff')
      .flatMap((agent) => selectedDocuments.map((document) => ({ agent, document })))
      .find(({ agent, document }) => (versions[agent.agentId]?.[document.documentId] ?? 0) < 2)
    if (invalidComparison) {
      onError(`Version Comparison requires version 2 or newer for ${invalidComparison.document.documentId}.`)
      return
    }

    setPending(null)
    setOptimisticRunning((current) => {
      const next = new Map(current)
      for (const documentId of documentIds) {
        next.set(documentId, new Set([...(next.get(documentId) ?? []), ...agentIds]))
      }
      return next
    })
    const owners: { jobId: string; documentId: string; agentId: string }[] = []
    try {
      const entries: WorkspaceRunEntry[] = []
      await runTracked(async () => {
        const jobs = []
        const startedAt = Date.now()
        for (const agent of selectedAgents) {
          if (!agent.pipelineId) throw new Error(`Agent ${agent.agentId} has no pipeline.`)
          for (const document of selectedDocuments) {
            const version = versions[agent.agentId]?.[document.documentId] ?? document.latestVersion
            const isComparison = agent.pipelineId === 'version-diff'
            const response = await runPipelines(apiClient, orgId, workspaceId, 'contract-review', {
              documents: [{
                documentId: document.documentId,
                ...(document.groupId ? { groupId: document.groupId } : {}),
              }],
              agentId: agent.agentId,
              ...(isComparison
                ? { compareVersions: { old: version - 1, new: version } }
                : { targetVersion: version }),
            })
            jobs.push(...response.jobs)
            for (const job of response.jobs) {
              const owner = { jobId: job.jobId, documentId: document.documentId, agentId: agent.agentId }
              owners.push(owner)
              jobOwnership.current.set(job.jobId, owner)
            }
            entries.push(...response.jobs.map((job) => ({
              jobId: job.jobId,
              agentLabel: agent.label,
              documentId: document.documentId,
              filename: document.latestFilename ?? document.name ?? document.documentId,
              version: isComparison ? `v${version} vs v${version - 1}` : `v${version}`,
              startedAt,
              status: 'PENDING',
            })))
          }
        }
        if (jobs.length === 0) throw new Error('No pipeline jobs were queued.')
        return { jobs }
      }, (response) => response.jobs.map((job) => job.jobId))
      status.addEntries(entries)
      onStatusChange()
    } catch (error) {
      for (const owner of owners) jobOwnership.current.delete(owner.jobId)
      clearOptimistic(documentIds.flatMap((documentId) => agentIds.map((agentId) => ({ documentId, agentId }))))
      // cm-coding: A monthly spending limit can reject a run before DocReview
      // creates any job to display. Show the backend's structured explanation
      // so users know processing was intentionally paused; Axios's generic HTTP
      // 409 text would make the policy decision look like a system failure.
      onError(workspaceRunErrorMessage(error))
    }
  }, [pending, agents, runTracked, status, clearOptimistic, apiClient, orgId, workspaceId, onStatusChange, onError])

  return {
    pending,
    pendingAgents: agents.filter((agent) => pending?.agentIds.includes(agent.agentId)),
    pendingDocuments: pending?.documents ?? [],
    optimisticRunning,
    isRunning,
    entries: status.entries,
    open,
    openDocuments,
    close: () => setPending(null),
    confirm,
    clearEntries: status.clearEntries,
  }
}
